import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, X } from 'lucide-react';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangeFilterProps {
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ onDateRangeChange }) => {
  const [startDate, setStartDate] = useState<Date | null>(startOfDay(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState<Date | null>(endOfDay(new Date()));
  const [selectedPreset, setSelectedPreset] = useState<string>('last30days');

  const presets = [
    { id: 'last7days', label: 'Last 7 Days', days: 7 },
    { id: 'last30days', label: 'Last 30 Days', days: 30 },
    { id: 'last90days', label: 'Last 90 Days', days: 90 },
    { id: 'custom', label: 'Custom Range', days: 0 }
  ];

  const handlePresetSelect = (presetId: string, days: number) => {
    setSelectedPreset(presetId);
    
    if (presetId === 'custom') {
      return; // Let user pick custom dates
    }

    const newEndDate = endOfDay(new Date());
    const newStartDate = startOfDay(subDays(newEndDate, days));
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onDateRangeChange(newStartDate, newEndDate);
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      const adjustedDate = startOfDay(date);
      setStartDate(adjustedDate);
      setSelectedPreset('custom');
      onDateRangeChange(adjustedDate, endDate);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      const adjustedDate = endOfDay(date);
      setEndDate(adjustedDate);
      setSelectedPreset('custom');
      onDateRangeChange(startDate, adjustedDate);
    }
  };

  const handleClearFilter = () => {
    const defaultStartDate = startOfDay(subDays(new Date(), 30));
    const defaultEndDate = endOfDay(new Date());
    
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setSelectedPreset('last30days');
    onDateRangeChange(defaultStartDate, defaultEndDate);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Time Period Filter</h3>
        </div>
        <button
          onClick={handleClearFilter}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          title="Reset to last 30 days"
        >
          <X className="w-4 h-4" />
          Reset
        </button>
      </div>

      {/* Preset Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePresetSelect(preset.id, preset.days)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPreset === preset.id
                ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Date Pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate || new Date()}
            dateFormat="MMM dd, yyyy"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholderText="Select start date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            maxDate={new Date()}
            dateFormat="MMM dd, yyyy"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholderText="Select end date"
          />
        </div>
      </div>

      {/* Selected Range Display */}
      {startDate && endDate && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Showing data from:</span>{' '}
            {startDate.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}{' '}
            to{' '}
            {endDate.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;